import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Check, X, Plus, Trash2, CheckCircle, List, ArrowLeftRight, Lightbulb } from "lucide-react";

/**
 * Composant éditeur de tableaux — Vrai/Faux, Multi-choix, Bijection.
 * Le tableau est directement éditable : inputs dans les cellules, radios visuels, ajout inline.
 *
 * Props inchangées : { config, onConfigChange, onRemove, isPreview }
 * Config inchangée : { type, titre, sousTitre, colonnes[], lignes[], bonnesReponses[] }
 */

// ─── Constantes module-level (hoistées hors du render) ───

const TABLE_TYPES = [
    { value: 'vraiFaux', label: 'Vrai / Faux', icon: CheckCircle, description: 'Chaque affirmation est vraie ou fausse' },
    { value: 'colonnesLibres', label: 'Multi-choix', icon: List, description: 'Colonnes personnalisables, une réponse par ligne' },
    { value: 'association', label: 'Bijection', icon: ArrowLeftRight, description: 'Chaque colonne ne peut être choisie qu\'une seule fois' },
];

const HINTS = {
    vraiFaux: 'Cliquez sur la colonne correcte pour chaque affirmation.',
    colonnesLibres: 'Ajoutez des colonnes et sélectionnez la bonne réponse par ligne.',
    association: 'Bijection : chaque colonne ne peut être sélectionnée qu\'une seule fois.',
};

const DEFAULT_CONFIG = {
    type: 'vraiFaux',
    titre: '',
    sousTitre: '',
    colonnes: ['Vrai', 'Faux'],
    lignes: [''],
    bonnesReponses: [0],
};

// ─── Sous-composant : sélecteur de type ───

const TypeSelector = memo(function TypeSelector({ currentType, onTypeChange }) {
    return (
        <div className="flex gap-2 px-3 py-2.5 border-b border-sky-100 flex-wrap">
            {TABLE_TYPES.map(({ value, label, icon: Icon, description }) => (
                <button
                    key={value}
                    type="button"
                    onClick={() => onTypeChange(value)}
                    title={description}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition border ${
                        currentType === value
                            ? 'bg-sky-50 border-sky-400 text-sky-700 shadow-sm'
                            : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                >
                    <Icon className="w-4 h-4" />
                    {label}
                </button>
            ))}
        </div>
    );
});

// ─── Composant principal ───

const TrueFalseTableElement = memo(function TrueFalseTableElement({ config, onConfigChange, onRemove, onValidChange, isPreview = false, compact = false }) {
    const currentConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);
    const addRowRef = useRef(null);
    const addColRef = useRef(null);

    // Sauvegarder la config initiale au mount (pour restauration au switch de type)
    const initialConfigRef = useRef(currentConfig);

    const isEditable = currentConfig.type !== 'vraiFaux';

    // ─── Validation : remonter isValid au parent ───
    const hasDuplicateLines = useMemo(() => {
        const filled = (currentConfig.lignes || []).filter(l => l.trim()).map(l => l.trim().toLowerCase());
        return filled.length !== new Set(filled).size;
    }, [currentConfig.lignes]);

    const hasDuplicateColonnes = useMemo(() => {
        const filled = (currentConfig.colonnes || []).filter(c => c.trim()).map(c => c.trim().toLowerCase());
        return filled.length !== new Set(filled).size;
    }, [currentConfig.colonnes]);

    // Bijection : en mode colonnesLibres, chaque colonne ne peut être sélectionnée qu'une seule fois
    const hasDuplicateBijection = useMemo(() => {
        if (currentConfig.type !== 'association') return false;
        const filledLines = (currentConfig.lignes || []).filter(l => l.trim());
        const answers = (currentConfig.bonnesReponses || []).slice(0, filledLines.length);
        // En bijection, tous les index comptent (0 = première colonne, pas "non coché")
        return answers.length !== new Set(answers).size;
    }, [currentConfig.type, currentConfig.lignes, currentConfig.bonnesReponses]);

    useEffect(() => {
        if (!onValidChange) return;
        const hasLines = currentConfig.lignes?.some(l => l.trim());
        onValidChange(Boolean(hasLines) && !hasDuplicateLines && !hasDuplicateColonnes && !hasDuplicateBijection);
    }, [currentConfig.lignes, onValidChange, hasDuplicateLines, hasDuplicateColonnes, hasDuplicateBijection]);

    // ─── Handlers (tous wrappés useCallback) ───

    // Changer le type de tableau — confirmation + restauration config initiale si retour au type original
    const handleTypeChange = useCallback((newType) => {
        if (newType === currentConfig.type) return;

        // Vérifier si des bonnes réponses sont configurées (pas toutes à 0)
        const hasConfiguredAnswers = currentConfig.bonnesReponses.some(r => r !== 0);
        if (hasConfiguredAnswers) {
            if (!window.confirm('Changer de type réinitialisera les réponses cochées. Continuer ?')) return;
        }

        // Si on revient au type initial → restaurer la config originale
        if (newType === initialConfigRef.current.type) {
            onConfigChange({ ...initialConfigRef.current });
            return;
        }

        // Sinon, reset classique
        let newColonnes = currentConfig.colonnes;

        if (newType === 'vraiFaux') {
            newColonnes = ['Vrai', 'Faux'];
        } else if (newType === 'colonnesLibres' || newType === 'association') {
            // Si colonnes sont les défauts Vrai/Faux → remplacer par colonnes libres
            if (currentConfig.colonnes.length === 2 &&
                currentConfig.colonnes[0] === 'Vrai' &&
                currentConfig.colonnes[1] === 'Faux') {
                newColonnes = ['Colonne 1', 'Colonne 2', 'Colonne 3'];
            }
        }

        onConfigChange({
            ...currentConfig,
            type: newType,
            colonnes: newColonnes,
            bonnesReponses: currentConfig.lignes.map(() => 0),
        });
    }, [currentConfig, onConfigChange]);

    // Modifier le texte d'une ligne
    const handleUpdateLigne = useCallback((index, value) => {
        const newLignes = [...currentConfig.lignes];
        newLignes[index] = value;
        onConfigChange({ ...currentConfig, lignes: newLignes });
    }, [currentConfig, onConfigChange]);

    // Supprimer une ligne
    const handleRemoveLigne = useCallback((index) => {
        if (currentConfig.lignes.length <= 1) return;
        onConfigChange({
            ...currentConfig,
            lignes: currentConfig.lignes.filter((_, i) => i !== index),
            bonnesReponses: currentConfig.bonnesReponses.filter((_, i) => i !== index),
        });
    }, [currentConfig, onConfigChange]);

    // Ajouter une ligne (depuis la phantom row)
    const handleAddLigne = useCallback((text) => {
        if (!text.trim()) return;
        onConfigChange({
            ...currentConfig,
            lignes: [...currentConfig.lignes, text.trim()],
            bonnesReponses: [...currentConfig.bonnesReponses, 0],
        });
    }, [currentConfig, onConfigChange]);

    // Modifier le texte d'une colonne
    const handleUpdateColonne = useCallback((index, value) => {
        const newColonnes = [...currentConfig.colonnes];
        newColonnes[index] = value;
        onConfigChange({ ...currentConfig, colonnes: newColonnes });
    }, [currentConfig, onConfigChange]);

    // Supprimer une colonne (min 2)
    const handleRemoveColonne = useCallback((index) => {
        if (currentConfig.colonnes.length <= 2) return;
        const newColonnes = currentConfig.colonnes.filter((_, i) => i !== index);
        // Réajuster les bonnes réponses si l'index supprimé décale les colonnes
        const newBonnesReponses = currentConfig.bonnesReponses.map(rep =>
            rep >= newColonnes.length ? newColonnes.length - 1 : (rep > index ? rep - 1 : rep)
        );
        onConfigChange({ ...currentConfig, colonnes: newColonnes, bonnesReponses: newBonnesReponses });
    }, [currentConfig, onConfigChange]);

    // Ajouter une colonne (depuis le bouton + dans le header)
    const handleAddColonne = useCallback(() => {
        // Si un input non contrôlé existe, utiliser sa valeur
        const inputValue = addColRef.current?.value?.trim();
        const newName = inputValue || `Col. ${currentConfig.colonnes.length + 1}`;
        onConfigChange({
            ...currentConfig,
            colonnes: [...currentConfig.colonnes, newName],
        });
        if (addColRef.current) addColRef.current.value = '';
    }, [currentConfig, onConfigChange]);

    // Définir la bonne réponse pour une ligne
    const handleSetBonneReponse = useCallback((ligneIndex, colonneIndex) => {
        const newBonnesReponses = [...currentConfig.bonnesReponses];

        // Bijection : si la colonne est déjà prise par une autre ligne → swap
        if (currentConfig.type === 'association') {
            const existingIndex = newBonnesReponses.findIndex((rep, i) =>
                i !== ligneIndex && rep === colonneIndex
            );
            if (existingIndex !== -1) {
                newBonnesReponses[existingIndex] = newBonnesReponses[ligneIndex];
            }
        }

        newBonnesReponses[ligneIndex] = colonneIndex;
        onConfigChange({ ...currentConfig, bonnesReponses: newBonnesReponses });
    }, [currentConfig, onConfigChange]);

    // Phantom row : ajout via Enter ou Blur
    const handleAddRowKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const value = addRowRef.current?.value;
            if (value?.trim()) {
                handleAddLigne(value);
                addRowRef.current.value = '';
            }
        }
    }, [handleAddLigne]);

    const handleAddRowBlur = useCallback(() => {
        const value = addRowRef.current?.value;
        if (value?.trim()) {
            handleAddLigne(value);
            addRowRef.current.value = '';
        }
    }, [handleAddLigne]);

    // ─── Tailles adaptées selon compact ───
    const radioSize = compact ? 'w-5 h-5' : 'w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8';
    const iconSize = compact ? 'w-2.5 h-2.5' : 'w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5';
    const cellPad = compact ? 'p-1' : 'p-1.5 sm:p-2';
    const tableText = compact ? 'text-[10px]' : 'text-xs sm:text-sm';
    const headerWidth = compact ? 'w-10' : 'w-14 sm:w-20';

    // ─── Rendu du radio visuel ───
    const renderRadio = (ligneIdx, colIdx, interactive = true) => {
        const isSelected = currentConfig.bonnesReponses[ligneIdx] === colIdx;
        const isVraiFaux = currentConfig.colonnes.length === 2 && currentConfig.type === 'vraiFaux';

        if (isSelected) {
            return (
                <button
                    type="button"
                    onClick={interactive ? () => handleSetBonneReponse(ligneIdx, colIdx) : undefined}
                    className={`${radioSize} rounded-full flex items-center justify-center mx-auto shadow-sm transition ${
                        interactive ? 'cursor-pointer' : 'cursor-default'
                    } ${isVraiFaux && colIdx === 1
                        ? 'bg-red-100 border-2 border-red-400'
                        : 'bg-green-100 border-2 border-green-500'
                    }`}
                >
                    {isVraiFaux && colIdx === 1
                        ? <X className={`${iconSize} text-red-500`} />
                        : <Check className={`${iconSize} text-green-600`} />
                    }
                </button>
            );
        }

        return (
            <button
                type="button"
                onClick={interactive ? () => handleSetBonneReponse(ligneIdx, colIdx) : undefined}
                className={`${radioSize} rounded-full border-2 border-gray-300 flex items-center justify-center mx-auto transition ${
                    interactive ? 'cursor-pointer hover:border-sky-400 hover:bg-sky-50' : 'cursor-default'
                }`}
            />
        );
    };

    // ─── Mode aperçu ───
    if (isPreview) {
        return (
            <div className={`bg-white rounded-xl border border-slate-200 ${compact ? 'p-1.5' : 'p-2 sm:p-4'}`}>
                {currentConfig.titre && (
                    <h3 className={`font-bold text-slate-800 mb-2 ${compact ? 'text-xs' : 'text-sm sm:text-base'}`}>{currentConfig.titre}</h3>
                )}
                <div className="overflow-x-auto">
                    <table className={`w-full border-collapse ${tableText} ${compact ? 'min-w-[200px]' : 'min-w-[300px]'}`}>
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                                <th className={`border border-blue-200 ${cellPad} text-left text-slate-600 font-medium`}>
                                    {currentConfig.sousTitre || 'Affirmations'}
                                </th>
                                {currentConfig.colonnes.map((col, idx) => (
                                    <th key={idx} className={`border border-blue-200 ${cellPad} text-center text-blue-700 font-bold ${headerWidth}`}>
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {currentConfig.lignes.filter(l => l.trim()).map((ligne, ligneIdx) => (
                                <tr key={ligneIdx} className={ligneIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                    <td className={`border border-blue-200 ${cellPad} text-slate-700`}>{ligne}</td>
                                    {currentConfig.colonnes.map((_, colIdx) => (
                                        <td key={colIdx} className={`border border-blue-200 ${cellPad} text-center`}>
                                            {renderRadio(ligneIdx, colIdx, false)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // ─── Mode configuration (tableau interactif unifié) ───
    return (
        <div className="border border-sky-200 rounded-xl overflow-hidden bg-white">
            {/* Sélecteur de type en cards */}
            <TypeSelector currentType={currentConfig.type} onTypeChange={handleTypeChange} />

            {/* Tableau interactif */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[10px] sm:text-xs md:text-sm min-w-[280px] sm:min-w-[320px]">
                    <thead>
                        <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                            {/* Header : label des affirmations */}
                            <th className="border border-blue-200 p-1 sm:p-1.5 md:p-2 text-left text-slate-600 font-medium min-w-[80px] sm:min-w-[160px]">
                                {currentConfig.sousTitre || 'Affirmations'}
                            </th>

                            {/* Headers des colonnes — éditables ou statiques selon le type */}
                            {currentConfig.colonnes.map((col, colIdx) => (
                                <th key={colIdx} className="border border-blue-200 p-0.5 sm:p-1 md:p-1.5 text-center w-12 sm:w-16 md:w-20 relative group/col">
                                    {isEditable ? (
                                        <div className="flex items-center justify-center gap-1">
                                            <input
                                                type="text"
                                                value={col}
                                                title={col}
                                                onChange={(e) => handleUpdateColonne(colIdx, e.target.value)}
                                                className="bg-transparent border-0 border-b border-dashed border-blue-300 text-center text-blue-700 font-bold text-[10px] sm:text-xs md:text-sm focus:outline-none focus:border-blue-500 w-full min-w-[30px] sm:min-w-[40px]"
                                            />
                                            {currentConfig.colonnes.length > 2 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveColonne(colIdx)}
                                                    className="opacity-0 group-hover/col:opacity-100 text-red-400 hover:text-red-600 transition absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border border-red-200"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-blue-700 font-bold text-xs sm:text-sm">{col}</span>
                                    )}
                                </th>
                            ))}

                            {/* Bouton + pour ajouter une colonne (Multi-choix / Bijection) */}
                            {isEditable && (
                                <th className="border border-blue-200 p-1 sm:p-1.5 w-10">
                                    <button
                                        type="button"
                                        onClick={handleAddColonne}
                                        className="w-7 h-7 mx-auto rounded-full border-2 border-dashed border-blue-300 flex items-center justify-center text-blue-400 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                    </button>
                                </th>
                            )}

                            {/* Colonne vide pour le bouton supprimer */}
                            <th className="w-8" />
                        </tr>
                    </thead>
                    <tbody>
                        {/* Lignes existantes — éditables */}
                        {currentConfig.lignes.map((ligne, ligneIdx) => (
                            <tr
                                key={ligneIdx}
                                className={`group/row ${ligneIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-sky-50/30 transition-colors`}
                            >
                                {/* Cellule texte — input inline */}
                                <td className="border border-blue-200 p-0.5 sm:p-1 md:p-1.5">
                                    <input
                                        type="text"
                                        value={ligne}
                                        onChange={(e) => handleUpdateLigne(ligneIdx, e.target.value)}
                                        placeholder={`Affirmation ${ligneIdx + 1}...`}
                                        className="w-full bg-transparent text-slate-700 text-[10px] sm:text-xs md:text-sm focus:outline-none placeholder-gray-300 px-0.5 sm:px-1"
                                    />
                                </td>

                                {/* Cellules radio — gros boutons visuels ● / ○ */}
                                {currentConfig.colonnes.map((_, colIdx) => (
                                    <td key={colIdx} className="border border-blue-200 p-0.5 sm:p-1 md:p-1.5 text-center">
                                        {renderRadio(ligneIdx, colIdx, true)}
                                    </td>
                                ))}

                                {/* Colonne vide si colonnes éditables */}
                                {isEditable && <td className="w-10" />}

                                {/* Bouton supprimer — visible au hover */}
                                <td className="w-8 text-center">
                                    {currentConfig.lignes.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveLigne(ligneIdx)}
                                            className="opacity-0 group-hover/row:opacity-100 text-red-400 hover:text-red-600 p-1 rounded transition"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}

                        {/* Phantom row — ajout inline (Enter ou Blur) */}
                        <tr className="bg-gray-50/50">
                            <td className="border border-dashed border-gray-200 p-1 sm:p-1.5">
                                <input
                                    ref={addRowRef}
                                    type="text"
                                    placeholder="+ Nouvelle affirmation..."
                                    onKeyDown={handleAddRowKeyDown}
                                    onBlur={handleAddRowBlur}
                                    className="w-full bg-transparent text-gray-400 placeholder-gray-300 text-xs sm:text-sm focus:outline-none italic focus:text-slate-700 focus:not-italic px-1"
                                />
                            </td>
                            {currentConfig.colonnes.map((_, colIdx) => (
                                <td key={colIdx} className="border border-dashed border-gray-200 p-1 sm:p-1.5" />
                            ))}
                            {isEditable && <td />}
                            <td />
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Hint contextuel */}
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50/50 border-t border-sky-100">
                <Lightbulb className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <p className="text-[11px] text-amber-700">{HINTS[currentConfig.type]}</p>
            </div>

            {/* Messages d'erreur doublons */}
            {hasDuplicateLines && (
                <p className="text-xs text-red-500 px-3 pb-2 flex items-center gap-1">
                    <span>⚠</span> Certaines lignes sont identiques — chaque ligne doit être unique.
                </p>
            )}
            {hasDuplicateColonnes && (
                <p className="text-xs text-red-500 px-3 pb-2 flex items-center gap-1">
                    <span>⚠</span> Certaines colonnes sont identiques — chaque colonne doit être unique.
                </p>
            )}
            {hasDuplicateBijection && (
                <p className="text-xs text-red-500 px-3 pb-2 flex items-center gap-1">
                    <span>⚠</span> En bijection, chaque colonne ne peut être sélectionnée qu'une seule fois.
                </p>
            )}
        </div>
    );
});

export default TrueFalseTableElement;
